RivalIQ.module('channels', function (channels, App, Backbone, Marionette, $, _, moment) {

    'use strict';

    const enums = RivalIQ.enums;

    /**
     * The list of supported sources (channels) to nag.
     * @type {*[]}
     */
    const sources = [
        RivalIQ.enums.sources.LINKEDIN,
        RivalIQ.enums.sources.FACEBOOK,
        RivalIQ.enums.sources.FACEBOOK_INSIGHTS,
        RivalIQ.enums.sources.TWITTER
    ];

    const DEFAULT_CREDENTIAL_NAG_TIMEOUT_HRS = channels.DEFAULT_CREDENTIAL_NAG_TIMEOUT_HRS = 4;
    const START_OF_EXPIRATION_WINDOW_DAYS = 21;

    const LAST_ATTEMPT = channels.LAST_ATTEMPT = 'Attempt';
    const NAG_EXPIRED_LAST = channels.NAG_EXPIRED_LAST = 'ExpiredLastNag';
    const NAG_EXPIRATION_LAST = channels.NAG_EXPIRATION_LAST = 'ExpirationLastNag';
    const NAG_ADDITIONAL_TOKEN_LAST = channels.NAG_ADDITIONAL_TOKEN_LAST = 'AdditionalTokenLastNag';
    const NAG_ADDITIONAL_TOKEN_TIMEOUT_HOURS = 24;

    const showingNags = {};

    /**
     * Returns a boolean indicating if the type of nag can be displayed.
     * @param source
     * @param nag
     * @param [hours]
     * @returns {boolean}
     */
    const canDisplayChannelCredentialNag = channels.canDisplayChannelCredentialNag = function (source, nag, hours) {

        const sourceDetails = enums.sourceDetails[source];
        const channel = sourceDetails.channel;
        const userSettings = RivalIQ.settings.userSettings || {};
        const value = userSettings[channel + nag];

        hours = (hours || DEFAULT_CREDENTIAL_NAG_TIMEOUT_HRS);

        return (!value || moment(value).add(hours, 'hours').toDate() < new Date());
    };

    const isBeforeNow = function (expiresAt) {
        if (!expiresAt) {
            return false;
        }
        return expiresAt < new Date();
    };

    /**
     * Updates a channel credential nag.
     * @param source
     */
    const updateChannelCredentialNag = channels.updateChannelCredentialNag = function (source, nag) {

        const sourceDetails = enums.sourceDetails[source];
        const channel = sourceDetails.channel;

        RivalIQ.execute('persistence:user:save', function (userSettings) {

            userSettings[channel + nag] = new Date();
        });
    };

    /**
     * Navigates the window location to the specified URL.
     * (exposed and abstracted for testing purposes only).
     * @type {Function}
     */
    channels.navigateToUrl = function (url) {

        window.location = url;
    };

    channels.getOAuthUrl = function (source, location) {
        // accepts location for unit testing.
        location = location || window.location;

        const sourceDetails = enums.sourceDetails[source];
        const pathname = location.pathname;
        const search = location.search;
        const hash = location.hash;
        let oauthRedirectParams = '';
        if (sourceDetails.oauthRedirectParams && !_.includes(search, sourceDetails.oauthRedirectParams)) {
            if (!search && !hash) {
                oauthRedirectParams = `?${sourceDetails.oauthRedirectParams}`;
            } else {
                oauthRedirectParams = `&${sourceDetails.oauthRedirectParams}`;
            }
        }

        const sanitizedPathname = encodeURIComponent([pathname, search, hash, oauthRedirectParams].map(encodeURI).join(''));

        return (`/oauth/${sourceDetails.oauthKey}?redirectPath=${sanitizedPathname}`);
    };

    // NOTE we only want to nag on "parent" channels in most cases (e.g.,
    // Facebook Insights should be the same as Facebook w/r/t auth), so we
    // should maintain an ignorelist to prevent excessive nagging
    const nagSourceIgnoreList = [
        RivalIQ.enums.sources.FACEBOOK_INSIGHTS
    ];

    function getChannelNameFromTokenSource(source, useFbName) {
        // NOTE tell the user Instagram connections are broken for expired fb tokens
        if (source === RivalIQ.enums.sources.FACEBOOK && !useFbName) {
            return RivalIQ.enums.sourceDetails[RivalIQ.enums.sources.INSTAGRAM].name;
        }

        return RivalIQ.enums.sourceDetails[source].name;
    }

    function getHandlesString(handles) {
        let handleStr = '';
        const numExpiredHandles = _.size(handles);
        if (numExpiredHandles === 1) {
            handleStr = `${_.first(handles)}`;
        } else if (numExpiredHandles === 2) {
            handleStr = `${handles[0]} and ${handles[1]}`;
        } else {
            handleStr = `${handles[0]} and ${numExpiredHandles - 1} others`;
        }

        return handleStr;
    }

    function displayBannerNotifications(notifications) {
        let headerText;
        let subtitleText;
        let summaryText;
        let buttonText;
        let reconnectCta;
        let briefHeaderText;

        const expiredTokens = notifications.expiredTokens;
        if (expiredTokens.length && RivalIQ.state.account.get('plan').has_multi_handle_private_basic) {
            const allExpiredHandles = _.uniq(_.flatten(_.map(expiredTokens, 'handles')));
            const displayFacebookHandles = _.every(expiredTokens, 'displayFacebookHandles');
            const numExpiredHandles = _.size(allExpiredHandles);
            const uniqueSources = _.uniq(_.map(expiredTokens, 'source'));
            const numSources = _.size(uniqueSources);
            const handlesStr = getHandlesString(allExpiredHandles);
            if (numSources === 1) {
                const channelName = getChannelNameFromTokenSource(uniqueSources[0], displayFacebookHandles);
                headerText = `Uh oh! The ${channelName} connection is broken.`;
                buttonText = `Reconnect ${channelName} Now`;
                // take user directly to oauth site if they own an expired token
                if (_.some(expiredTokens, 'userOwnsExpiredToken')) {
                    reconnectCta = channels.getOAuthUrl(uniqueSources[0]);
                }

                if (uniqueSources[0] === RivalIQ.enums.sources.FACEBOOK && !displayFacebookHandles) {
                    // complain about losing ig data for unconnected fb tokens
                    const baseText = `We lose Instagram Stories and audience data each day ${handlesStr}`;
                    if (numExpiredHandles === 1) {
                        // We lose Instagram Stories and audience data each day [Handle Name] is unconnected.
                        subtitleText = `${baseText} is unconnected.`;
                    } else {
                        // We lose Instagram Stories and audience data each day [Handle Name1] and [HandleName2] are unconnected.
                        subtitleText = `${baseText} are unconnected.`;
                    }
                    // this summary text is a condensed message that appears on ipad breakpoint
                    summaryText = `Stories and audience data is lost when unconnected.`;
                }
            } else {
                // The connections are broken for [Channel name], [Channel name], [Channel name], and [Channel name].
                const baseText = `${numSources > 2 ? 'Connections' : 'The connections'} are broken for`;
                const baseBriefHeaderText = `Connections are broken for`;
                if (numSources === 2) {
                    const channelListStr = `${getChannelNameFromTokenSource(uniqueSources[0])} and ${getChannelNameFromTokenSource(uniqueSources[1])}`;
                    headerText = `${baseText} ${channelListStr}.`;
                    briefHeaderText = `${baseBriefHeaderText} ${channelListStr}`;
                } else {
                    const andIndex = _.size(uniqueSources) > 2 ? _.size(uniqueSources) - 1 : -1;
                    const channelNames = _.reduce(uniqueSources, (str, source, i) => {
                        if (i === 0) {
                            str += ` ${getChannelNameFromTokenSource(source)}`;
                        } else if (andIndex === i) {
                            str += `, and ${getChannelNameFromTokenSource(source)}`;
                        } else {
                            str += `, ${getChannelNameFromTokenSource(source)}`;
                        }
                        return str;
                    }, '');
                    headerText = `${baseText} ${channelNames}.`;
                    briefHeaderText = `${baseBriefHeaderText} ${channelNames}.`;
                }
            }

            if (!summaryText) {
                const baseText = `We lose follower counts and vital audience data each day ${handlesStr}`;
                if (uniqueSources[0] === RivalIQ.enums.sources.TWITTER) {
                    // Reconnect XXX and YYY for deeper metrics, including impressions and clicks.
                    subtitleText = `Reconnect ${handlesStr} for deeper metrics, including impressions and clicks.`;
                } else if (numExpiredHandles === 1) {
                    // We lose follower counts and vital audience data each day [Handle Name] is unconnected.
                    subtitleText = `${baseText} is unconnected.`;
                } else {
                    // We lose follower counts and vital audience data each day [Handle Name1] and [HandleName2] are unconnected.
                    subtitleText = `${baseText} are unconnected.`;
                }

                summaryText = `Vital audience data is lost when unconnected.`;
                buttonText ||= `Reconnect Your Data Now`;
                reconnectCta ||= '/account/services';
            }

            const shellEnums = RivalIQ.apps.Shell.enums;
            const attributes = {
                headerText,
                subtitleText,
                summaryText,
                buttonText,
                reconnectCta,
                briefHeaderText: briefHeaderText || headerText,
                expiredSources: uniqueSources,
                size: shellEnums.navigation_size.TALL
            };
            RivalIQ.execute('show:notification', shellEnums.notifications.EXPIRED_TOKENS, attributes);
        }
    }

    function displayNagNotifications(nags, showAdditionalTokenRequest) {
        _.each(nags, (attributes) => {
            const source = attributes.source;
            let wasUpdated = false;

            const view = new channels.views.Nag({
                model: new Backbone.Model(attributes)
            });

            // NOTE we listen here so that we can capture click events
            // on the special links in the nag (this enables us to actually do
            // the credential update via OAuth)
            view.listenTo(view, 'update', function () {

                const url = channels.getOAuthUrl(source);

                RivalIQ.logger.info(`channel credentials updating at '${window.location.pathname}'`);

                updateChannelCredentialNag(source, LAST_ATTEMPT);

                // NOTE this is our flag to indicate whether or not this
                // channel credential has been updated - the flag will be
                // utilized when the view is destroyed
                wasUpdated = true;
                channels.navigateToUrl(url);
            });

            // NOTE we listen for destroy so that we can save the nag dismissal
            // if there happened to be one, or take no action if the credentials
            // were updated
            view.listenTo(view, 'destroy', function () {

                showingNags[source] = false;
                if (wasUpdated) {
                    return;
                }

                let nag = null;
                if (_.has(attributes, 'expiring')) {
                    nag = NAG_EXPIRATION_LAST;
                } else if (showAdditionalTokenRequest) {
                    nag = NAG_ADDITIONAL_TOKEN_LAST;
                }

                RivalIQ.logger.info(`channel updateChannelCredentialNag prompting ${nag}`);
                updateChannelCredentialNag(source, nag);
            });

            RivalIQ.logger.info('channel credentials prompting');

            let modalOptions = null;
            if (attributes.additionalTokenRequest) {
                modalOptions = {
                    autoDestructDelay: -1
                };
            }
            RivalIQ.execute('alert:info', view, modalOptions);
            showingNags[source] = true;
        });
    }

    /**
     * Checks channel credentials and nags if need be.
     * @param sources
     */
    const checkAllChannelCredentials = channels.checkAllChannelCredentials = function (sources, showAdditionalTokenRequest) {

        if (RivalIQ.state.user.isActive()) {
            sources = _.difference(sources, nagSourceIgnoreList);

            const notifications = _.reduce(sources, (notifications, source) => {
                const sourceDetails = enums.sourceDetails[source];
                const channel = sourceDetails.channel;
                const channelName = sourceDetails.name;
                const userSettings = RivalIQ.settings.userSettings || {};
                const channelAttempt = userSettings[channel + LAST_ATTEMPT];
                const tokenSummaries = _.map(RivalIQ.data.channels.get(channel).getUniqueConnections(), 'attributes');

                RivalIQ.logger.info(`checking channel credentials for '${sourceDetails.name}'`);

                let shouldNotify = false;

                if (channelAttempt && moment(channelAttempt).add(10, 'seconds').toDate() > new Date()) {
                    // still populating data for a channel
                    notifications.nags.push({
                        sourceDetails: sourceDetails,
                        source: source,
                        populating: true
                    });
                    shouldNotify = true;
                    RivalIQ.logger.info('channel credentials populating');
                } else {
                    _.each(tokenSummaries, function (channelTokenSummary) {
                        const tokenExpiresAt = channelTokenSummary.refreshTokenExpiresAt || channelTokenSummary.expiresAt;
                        const startOfExpirationWindow = moment(tokenExpiresAt).subtract(START_OF_EXPIRATION_WINDOW_DAYS, 'days');
                        // token expired already
                        if (channelTokenSummary.expired) {
                            if (canDisplayChannelCredentialNag(source, NAG_EXPIRED_LAST)) {
                                // refresh channel connection
                                let handles = channelTokenSummary.screen_name ? [channelTokenSummary.screen_name] : _.map(channelTokenSummary.presences, 'handle');
                                let displayFacebookHandles = false;
                                if (source === RivalIQ.enums.sources.FACEBOOK && !_.size(handles)) {
                                    displayFacebookHandles = true;
                                    handles = _.map(channelTokenSummary.facebookPresences, 'handle');
                                }

                                // don't show banner if there are no presences mapped
                                if (_.size(handles)) {
                                    const expiredTokenConfig = {
                                        source: source,
                                        handles: handles,
                                        account_user_id: channelTokenSummary.account_user_id,
                                        userOwnsExpiredToken: channelTokenSummary.userOwnsExpiredToken,
                                        displayFacebookHandles
                                    };

                                    notifications.banner.expiredTokens.push(expiredTokenConfig);
                                }

                                shouldNotify = true;
                                RivalIQ.logger.info(`token credentials expired; ${channelTokenSummary.screen_name}`);
                            }
                        } else if (tokenExpiresAt && isBeforeNow(startOfExpirationWindow)) {
                            if (canDisplayChannelCredentialNag(source, NAG_EXPIRATION_LAST)) {
                                // nudge channel
                                notifications.nags.push({
                                    source: source,
                                    name: channelName,
                                    expiring: {
                                        handle: channelTokenSummary.screen_name
                                    }
                                });
                                shouldNotify = true;
                                RivalIQ.logger.info(`channel credentials expiring; ${channelTokenSummary.screen_name}`);
                            }
                        }
                    });
                }

                if (!shouldNotify && showAdditionalTokenRequest &&
                    canDisplayChannelCredentialNag(source, NAG_ADDITIONAL_TOKEN_LAST, NAG_ADDITIONAL_TOKEN_TIMEOUT_HOURS)) {

                    notifications.nags.push({
                        additionalTokenRequest: true,
                        name: channelName,
                        sourceDetails: sourceDetails
                    });
                    shouldNotify = true;
                    RivalIQ.logger.info('request additional channel credentials');
                }

                if (!shouldNotify) {
                    RivalIQ.logger.info('channel credentials do not require nagging');
                }

                return notifications;
            }, {
                nags: [],
                banner: {
                    expiredTokens: [],
                    expiringTokens: []
                }
            });

            displayBannerNotifications(notifications.banner);
            displayNagNotifications(notifications.nags, showAdditionalTokenRequest);
        }
    };

    RivalIQ.commands.setHandler('channels:nag', _.bind(checkAllChannelCredentials, null, sources));

    // NOTE we set up individual channel nag commands mechanically here
    _.each(sources, function (source) {

        const sourceDetails = enums.sourceDetails[source];
        RivalIQ.commands.setHandler(`channels:nag:${sourceDetails.channel}`,
            _.bind(checkAllChannelCredentials, null, [source]));
    });

    /**
     * Redirects to the default location when the channel is hidden.
     * @param source
     */
    channels.enforceHiddenChannel = function (source) {

        // NOTE we want to show channels w/ missing tokens and channels not on
        // the current plan
        if (RivalIQ.state.landscape.channelHidden(source)) {

            window.location = RivalIQ.state.product.get('defaultRedirectLocation');
            return true;
        }

        return false;
    };

}, window.moment);
